import type { Feature, FeatureCollection, MultiPolygon, Polygon } from "geojson";
import { feature } from "topojson-client";
import type { GeometryCollection, Topology } from "topojson-specification";
// Natural Earth's 1:50m land (public domain, via world-atlas): fetched once, when a trip card first
// needs it, not bundled into the app.
import landUrl from "world-atlas/land-50m.json?url";

// The land, for the trip cards' little maps: polygons as rings of [lng, lat], each with its box so a
// card only draws what it shows.

export interface Land {
  rings: [number, number][][];
  box: [number, number, number, number];
}

let loading: Promise<Land[]> | null = null;

export function loadLand(): Promise<Land[]> {
  loading ??= fetch(landUrl)
    .then((r) => {
      if (!r.ok) throw new Error(`coastline: ${r.status}`);
      return r.json() as Promise<Topology<{ land: GeometryCollection }>>;
    })
    .then((topology) => {
      const land = feature(topology, topology.objects.land) as
        | FeatureCollection<Polygon | MultiPolygon>
        | Feature<Polygon | MultiPolygon>;
      const features = "features" in land ? land.features : [land];
      return features.flatMap((f) =>
        (f.geometry.type === "Polygon" ? [f.geometry.coordinates] : f.geometry.coordinates).map((polygon) => {
          const rings = polygon as [number, number][][];
          const outer = rings[0] ?? [];
          const lngs = outer.map((p) => p[0]);
          const lats = outer.map((p) => p[1]);
          return {
            rings,
            box: [Math.min(...lngs), Math.min(...lats), Math.max(...lngs), Math.max(...lats)] as Land["box"],
          };
        }),
      );
    })
    .catch((error: unknown) => {
      // No coastline this time (the cards show sea and dots); try again on the next visit.
      console.warn(error);
      loading = null;
      return [];
    });
  return loading;
}
